import { atom, useRecoilState, useResetRecoilState } from "recoil";
import { 
    readProfileListAPI, 
    updateProfileAPI, 
    ProfileType 
} from "@/Data/User/Profile";
import { userDatabase } from "@/Data/User/Database";
import { useCallback } from "react";
import ss from "../../Data/Session";
import { randomString } from "@/Functions";

// Validation function for user profile data
const validateProfile = (profile: any): profile is ProfileType => {
    return profile && 
           typeof profile.addressid === 'string' && 
           typeof profile.userid === 'string' && 
           typeof profile.name === 'string' && 
           (profile.image === null || typeof profile.image === 'string');
};

export const AUTHOR_SUBSCRIPTION = "author-subscription";

// Main state atom for user data with offline support
const userState = atom({
    key: 'userState'+randomString(4),
    default: {
        profiles: {} as Record<string, ProfileType>,
        loading: false,
        error: null as string | null,
    }, effects: [
        ({ setSelf }) => {
            const initDatabase = async () => {
                try {
                    await userDatabase.init();
                    
                    // Load cached profiles on init
                    const cached = await userDatabase.getAllProfiles();
                    
                    // Validate profiles before using
                    const validProfiles = cached.filter(validateProfile);
                    
                    if (validProfiles.length !== cached.length) {
                        console.warn(`Filtered out ${cached.length - validProfiles.length} invalid user profiles from database`);
                    }
                    
                    // Convert to record object for quick access
                    const profiles = Object.fromEntries(validProfiles.map((profile) => ([profile.addressid, profile])));
                    
                    setSelf({ profiles, loading: false, error: null });
                
                } catch (error) {
                    console.error('Failed to initialize user database:', error);
                    setSelf({ profiles: {}, error: 'Failed to initialize user database', loading: false });
                }
            };
            
            initDatabase();
        }
    ]
});

/**
 * Hook for managing user profiles with offline support
 */
export const useUsers = () => {
    const [state, setState] = useRecoilState(userState);
    const overwriteState = useCallback((newState: Partial<typeof state>) => setState( prev => ({ ...prev, ...newState }) ), [setState]);

    const { profiles, loading, error } = state;
    
    // Sync multiple profiles from API (void, updates state)
    const syncProfiles = useCallback(async (addressids: string[]): Promise<void> => {
        // Guard clause - early return if no IDs provided or empty array
        if (!addressids || !Array.isArray(addressids) || addressids.length === 0) {
            console.warn('No address IDs provided to syncProfiles');
            return;
        }
        
        // Validate address IDs are strings
        const validAddressIds = addressids.filter(id => typeof id === 'string');
        if (validAddressIds.length !== addressids.length) {
            console.warn(`Filtered out ${addressids.length - validAddressIds.length} invalid address IDs`);
            if (validAddressIds.length === 0) return;
        }
        
        overwriteState({ loading: true, error: null });
        
        try {
            // Call API directly
            const onlineProfiles = await readProfileListAPI(validAddressIds);
            
            // Guard clause - early return if no profiles received
            if (!onlineProfiles || !Array.isArray(onlineProfiles) || onlineProfiles.length === 0) {
                console.warn('No profiles received from API');
                overwriteState({ loading: false });
                return;
            }
            
            // Validate profiles before saving to database
            const validProfiles = onlineProfiles.filter(validateProfile);
            
            if (validProfiles.length !== onlineProfiles.length) {
                console.warn(`Filtered out ${onlineProfiles.length - validProfiles.length} invalid profiles from API response`);
                console.log('Invalid profiles:', onlineProfiles.filter(profile => !validateProfile(profile)));
            }
            
            // Guard clause - early return if all profiles were invalid
            if (validProfiles.length === 0) {
                console.error('No valid profiles received from API');
                overwriteState({ error: "No valid profiles available", loading: false });
                return;
            }
            
            // Save valid profiles to SQLite database
            await userDatabase.saveProfiles(validProfiles);

            // Create a map of address ID to profile for quick lookup
            const newProfiles = Object.fromEntries(validProfiles.map(profile => [profile.addressid, profile]));
            
            // Update state with validated profiles
            setState(prev => ({
                ...prev,
                profiles: { ...prev.profiles, ...newProfiles },
                loading: false,
            }));
            
        } catch (error) {
            console.error('Error syncing profiles:', error);
            // API failed, mark as offline
            overwriteState({ 
                error: error instanceof Error ? error.message : "Failed to sync profiles from API", 
                loading: false 
            });
        }
    }, [setState, overwriteState]);
    
    // Update user profile
    const updateProfile = useCallback(async (profileData: Partial<ProfileType>): Promise<boolean> => {
        // Guard clause - validate input data
        if (!profileData || typeof profileData !== 'object') {
            console.error('Invalid profile data provided');
            return false;
        }
        
        overwriteState({ loading: true, error: null });
        
        try {
            // Get the current user ID
            const addressid = ss.getCurrentSession().addressid;
            
            // Guard clause - early return if no address ID
            if (!addressid) {
                console.error('No user ID available for profile update');
                overwriteState({ loading: false, error: "No user ID available" });
                return false;
            }
            
            // Get the current profile
            const currentProfile = profiles[addressid];
            
            // Guard clause - early return if no current profile
            if (!currentProfile) {
                console.error('Current profile not found for address ID:', addressid);
                overwriteState({ loading: false, error: "Profile not found" });
                return false;
            }
            
            // Validate current profile
            if (!validateProfile(currentProfile)) {
                console.error('Current profile is invalid:', currentProfile);
                overwriteState({ loading: false, error: "Current profile is invalid" });
                return false;
            }
            
            // Create updated profile
            const updatedProfile: ProfileType = {
                ...currentProfile,
                ...profileData
            };
            
            // Validate merged profile
            if (!validateProfile(updatedProfile)) {
                console.error('Merged profile data would result in invalid profile:', updatedProfile);
                overwriteState({ loading: false, error: "Updated profile would be invalid" });
                return false;
            }
            
            // Try API call
            try {
                const result = await updateProfileAPI(profileData);
                
                // Validate API result
                if (result && typeof result !== 'string' && validateProfile(result)) {
                    // If succeeded, update with server data
                    await userDatabase.saveProfiles([result]);
                    
                    setState(prev => {

                        let newProfiles = { ...prev.profiles };
                        if (addressid !== result.addressid) {
                            delete newProfiles[addressid]; // Remove old addressid if it changed
                        }
                        newProfiles[result.addressid] = result; // Add or update with new profile

                        return {
                            ...prev,
                            profiles: newProfiles,
                            loading: false
                        }
                    });
                    
                    return true;
                } else {
                    // Fall back to local update if API response is invalid
                    console.warn('API returned invalid profile data:', result);
                    
                    return false;
                }
            } catch {
                console.warn('Failed to update profile on server');
                return false;
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            overwriteState({
                error: error instanceof Error ? error.message : "Failed to update profile",
                loading: false
            });
            return false;
        }
    }, [profiles, setState, overwriteState]);
    
    // Check if addressid already exists
    const checkAddressIdExists = useCallback(async (addressid: string): Promise<boolean> => {
        // Guard clause - validate input
        if (!addressid || typeof addressid !== 'string') {
            console.warn('Invalid addressid provided to checkAddressIdExists');
            return false;
        }
        
        // First check local cache
        if (profiles[addressid]) {
            return true;
        }
        
        try {
            // Try to fetch profile from API
            const profileResult = await readProfileListAPI([addressid]);
            
            // If we get a profile back, the addressid exists
            if (profileResult && profileResult.length > 0) {
                // Cache the profile for future use
                const validProfiles = profileResult.filter(validateProfile);
                if (validProfiles.length > 0) {
                    await userDatabase.saveProfiles(validProfiles);
                    const newProfiles = Object.fromEntries(validProfiles.map(profile => [profile.addressid, profile]));
                    setState(prev => ({
                        ...prev,
                        profiles: { ...prev.profiles, ...newProfiles }
                    }));
                }
                return true;
            }
            
            // No profile found means addressid is available
            return false;
        } catch {
            // 404 or null response means addressid is available (good case)
            // Other errors (network, server) should be optimistic (allow user to try)
            console.log('Addressid check result: available (API returned no profile)');
            return false;
        }
    }, [profiles, setState]);
    
    const reset = useResetRecoilState(userState);

    return {
        // Data objects
        profiles,
        loading,
        error,
        
        // Sync functions
        syncProfiles,
        
        // Action that updates local and tries API
        updateProfile,
        
        // Validation functions
        checkAddressIdExists,

        reset
    };
};